import React from 'react';
import './LegalPages.css';

const RefundPolicy: React.FC = () => {
  return (
    <div className="legal-page">
      <div className="container">
        <h1>Refund & Dispute Policy</h1>
        <p className="last-updated">Last Updated: January 23, 2026</p>

        <section>
          <h2>1. Overview</h2>
          <p>
            At Cruise Spotter Connect, we strive for fairness and transparency in our billing. This
            policy outlines how to dispute charges and request refunds or credits for calls that do
            not meet our billable criteria.
          </p>
        </section>

        <section>
          <h2>2. Our Commitment</h2>
          <p>
            We only charge for calls that meet ALL of our billable criteria. If a call was incorrectly
            charged, we will issue a credit or refund promptly. We want you to feel confident that
            you're only paying for qualified leads.
          </p>
        </section>

        <section>
          <h2>3. Billable Call Criteria (Reminder)</h2>
          <p>A call must meet ALL of the following to be billable:</p>
          <ul>
            <li>Duration of 90+ seconds</li>
            <li>Cruise-related inquiry</li>
            <li>Legitimate customer (not spam)</li>
            <li>First contact from that phone number</li>
            <li>Successfully connected conversation</li>
          </ul>
        </section>

        <section>
          <h2>4. Valid Reasons for Dispute</h2>
          <p>You may dispute a charge for the following reasons:</p>
          <div className="reason-box valid">
            <h3>✓ Valid Dispute Reasons</h3>
            <ul>
              <li><strong>Call Under 90 Seconds:</strong> The call was shorter than 90 seconds</li>
              <li><strong>Wrong Call Type:</strong> Caller was asking about non-cruise travel or other topics</li>
              <li><strong>Spam/Robocall:</strong> The call was clearly spam, a robocall, or prank</li>
              <li><strong>Technical Issue:</strong> Call quality was so poor you couldn't have a conversation</li>
              <li><strong>Duplicate Call:</strong> Same customer called within 24 hours and you were charged twice</li>
              <li><strong>Call Not Connected:</strong> You didn't answer or call failed to connect</li>
              <li><strong>Follow-Up Call:</strong> This was a follow-up call from a customer already assigned to you</li>
              <li><strong>Billing Error:</strong> Charge amount is incorrect or call was billed to wrong account</li>
            </ul>
          </div>
        </section>

        <section>
          <h2>5. Invalid Reasons for Dispute</h2>
          <p>The following are NOT valid reasons for dispute:</p>
          <div className="reason-box invalid">
            <h3>✗ Invalid Dispute Reasons</h3>
            <ul>
              <li><strong>Customer Didn't Book:</strong> We charge for qualified calls, not bookings. Conversion is your responsibility.</li>
              <li><strong>Low Budget Customer:</strong> Customer wanted a cheaper cruise than you wanted to sell</li>
              <li><strong>Shopper Mentality:</strong> Customer wanted to compare prices before deciding</li>
              <li><strong>Changed Their Mind:</strong> Customer decided not to book after the call</li>
              <li><strong>Couldn't Help Them:</strong> Customer wanted something outside your expertise</li>
              <li><strong>Poor Communication:</strong> Language barrier or difficulty understanding customer (unless extreme and lasted less than 90 seconds)</li>
              <li><strong>Already Booked:</strong> Customer mentioned they already had a cruise booked but still discussed cruising with you for 90+ seconds</li>
            </ul>
          </div>
          <p className="note">
            <strong>Important:</strong> We provide leads, not guaranteed sales. If a call met all
            billable criteria but didn't result in a booking, that is not grounds for a refund.
          </p>
        </section>

        <section>
          <h2>6. How to Dispute a Charge</h2>
          <div className="process-steps-legal">
            <div className="step-legal">
              <h3>Step 1: Review Your Invoice</h3>
              <p>
                Check your monthly invoice and identify the specific call(s) you want to dispute.
                Note the date, time, and call ID.
              </p>
            </div>
            <div className="step-legal">
              <h3>Step 2: Listen to Recording</h3>
              <p>
                Access the call recording in your dashboard (available for 90 days). Verify that
                the call doesn't meet billable criteria.
              </p>
            </div>
            <div className="step-legal">
              <h3>Step 3: Submit Dispute</h3>
              <p>
                Email <a href="mailto:billing@cruisespotterconnect.com">billing@cruisespotterconnect.com</a> within
                15 days of the invoice date with:
              </p>
              <ul>
                <li>Your account name and email</li>
                <li>Invoice number</li>
                <li>Call ID(s) being disputed</li>
                <li>Reason for dispute (reference valid reasons above)</li>
                <li>Any supporting details</li>
              </ul>
            </div>
            <div className="step-legal">
              <h3>Step 4: We Review</h3>
              <p>
                We will review the call recording and details within 3 business days and respond
                with our decision.
              </p>
            </div>
          </div>
        </section>

        <section>
          <h2>7. Dispute Window</h2>
          <p>
            <strong>You must submit disputes within 15 days of the invoice date.</strong> Disputes
            submitted after 15 days may not be accepted. This allows us to review recordings while
            they're still available (90-day retention).
          </p>
        </section>

        <section>
          <h2>8. Review Process</h2>
          <p>When you submit a dispute:</p>
          <ul>
            <li>We will acknowledge receipt within 1 business day</li>
            <li>We will review the call recording and metadata</li>
            <li>We will respond with a decision within 3 business days</li>
            <li>If approved, credit will be applied to your next invoice or refunded</li>
            <li>If denied, we will explain why and provide supporting evidence</li>
          </ul>
        </section>

        <section>
          <h2>9. Credits and Refunds</h2>
          <h3>Approved Disputes</h3>
          <p>If your dispute is approved:</p>
          <ul>
            <li>Credits will be applied to your next monthly invoice</li>
            <li>If you prefer a refund, one will be issued to your original payment method</li>
            <li>Refunds are processed within 5-7 business days</li>
            <li>You will receive confirmation via email</li>
          </ul>

          <h3>Partial Disputes</h3>
          <p>
            If you dispute multiple calls and some are valid while others are not, we will credit
            only the valid disputes.
          </p>
        </section>

        <section>
          <h2>10. Abuse Prevention</h2>
          <p>We take dispute abuse seriously. If you:</p>
          <ul>
            <li>Submit multiple invalid disputes</li>
            <li>Dispute calls that clearly meet billable criteria</li>
            <li>Use disputes to avoid paying for legitimate calls</li>
          </ul>
          <p>
            We reserve the right to suspend or terminate your account. We understand occasional
            disagreements, but systematic abuse will not be tolerated.
          </p>
        </section>

        <section>
          <h2>11. Escalation</h2>
          <p>
            If you disagree with our dispute decision, you may escalate to management by replying to
            the dispute response email. A senior team member will review within 5 business days. This
            decision is final.
          </p>
        </section>

        <section className="cancelation-section">
          <h1>Cancelation Policy</h1>

          <h2>12. No Contracts</h2>
          <p>
            We operate on a month-to-month basis. There are no long-term contracts, no commitments,
            and no cancelation fees. You are free to stop using our service at any time.
          </p>
        </section>

        <section>
          <h2>13. How to Pause Service</h2>
          <p>
            If you want to temporarily stop receiving calls without fully canceling your account:
          </p>
          <ul>
            <li>Log into your dashboard</li>
            <li>Toggle your availability to "Unavailable"</li>
            <li>You will not receive any calls or charges while unavailable</li>
            <li>Toggle back to "Available" whenever you're ready to resume</li>
            <li>Your account remains active and you keep access to your dashboard and history</li>
          </ul>
          <p>
            <strong>This is the recommended approach if you need a temporary break.</strong>
          </p>
        </section>

        <section>
          <h2>14. How to Fully Cancel</h2>
          <p>To permanently close your account:</p>
          <ul>
            <li>Email <a href="mailto:support@cruisespotterconnect.com">support@cruisespotterconnect.com</a> or <a href="mailto:agents@cruisespotterconnect.com">agents@cruisespotterconnect.com</a></li>
            <li>Request account cancelation</li>
            <li>We will confirm cancelation within 1 business day</li>
            <li>Your account will be closed immediately</li>
            <li>You will not receive any more calls</li>
          </ul>
        </section>

        <section>
          <h2>15. Final Billing After Cancelation</h2>
          <p>Important billing information for canceled accounts:</p>
          <ul>
            <li>You are still responsible for charges incurred before cancelation</li>
            <li>If you cancel mid-month, you'll receive a final invoice on the 1st of the next month</li>
            <li>This final invoice covers all calls from before your cancelation date</li>
            <li>You can still dispute charges from your final invoice using the normal process</li>
            <li>After the final invoice is paid, you have no further financial obligations</li>
          </ul>
        </section>

        <section>
          <h2>16. No Cancelation Fees</h2>
          <p>
            We do not charge any fees for canceling your account. You simply pay for the calls you
            received before cancelation. That's it.
          </p>
        </section>

        <section>
          <h2>17. Reactivation</h2>
          <p>Changed your mind? You can reactivate your account anytime:</p>
          <ul>
            <li>Contact support at <a href="mailto:agents@cruisespotterconnect.com">agents@cruisespotterconnect.com</a></li>
            <li>Request reactivation</li>
            <li>We'll restore your account (usually within 24 hours)</li>
            <li>Your call history and settings will be preserved for 90 days after cancelation</li>
            <li>After 90 days, you'll need to create a new account</li>
          </ul>
        </section>

        <section>
          <h2>18. Data After Cancelation</h2>
          <p>When you cancel:</p>
          <ul>
            <li>Call recordings are retained for 90 days, then deleted</li>
            <li>Account data is retained for 7 years for tax/legal purposes</li>
            <li>You can request a data export before canceling</li>
            <li>Customer phone numbers assigned to you remain yours (we won't reassign them)</li>
          </ul>
        </section>

        <section>
          <h2>19. Contact for Billing & Cancelation</h2>
          <div className="contact-info">
            <p><strong>For Disputes:</strong></p>
            <p>Email: <a href="mailto:billing@cruisespotterconnect.com">billing@cruisespotterconnect.com</a></p>

            <p><strong>For Cancelation:</strong></p>
            <p>Email: <a href="mailto:support@cruisespotterconnect.com">support@cruisespotterconnect.com</a></p>
            <p>Or: <a href="mailto:agents@cruisespotterconnect.com">agents@cruisespotterconnect.com</a></p>

            <p>Monday-Friday, 9 AM - 6 PM EST</p>
          </div>
        </section>
      </div>
    </div>
  );
};

export default RefundPolicy;
